import React, { useEffect, useState } from 'react';
import { Button } from './index';
import { getMentorSlots, MentorWithSlots } from '../services/mentor-availability-service';
import { bookSlot, MentoringRequestDoc } from '../services/mentoring-request-service';

function fmtSlot(scheduledAt: string, startTime: string, endTime: string): string {
  const d = new Date(scheduledAt);
  const date = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  return `${date}, ${startTime} – ${endTime}`;
}

interface ConfirmState {
  mentor: MentorWithSlots['mentor'];
  slot: MentorWithSlots['slots'][0];
}

interface Props {
  existingRequest?: MentoringRequestDoc | null;
  onBooked?: () => void;
}

const MentorSlotBooker: React.FC<Props> = ({ existingRequest, onBooked }) => {
  const [mentors, setMentors] = useState<MentorWithSlots[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (existingRequest) return;
    (async () => {
      try {
        let result: MentorWithSlots[] = [];
        for (const days of [3, 7, 14]) {
          result = await getMentorSlots(days);
          if (result.some(m => m.slots.some(s => !s.booked))) break;
        }
        setMentors(result);
      } catch {
        setError('Failed to load available slots.');
      } finally {
        setLoading(false);
      }
    })();
  }, [existingRequest]);

  if (existingRequest) {
    const statusLabel: Record<string, string> = {
      pending:   'Awaiting mentor response',
      accepted:  'Session confirmed',
      scheduled: 'Session scheduled by admin',
      rejected:  'Request declined — book another slot',
      expired:   'Request expired — book another slot',
    };
    const isActive = ['pending', 'accepted', 'scheduled'].includes(existingRequest.status);
    return (
      <div className="border border-line rounded-xl p-6">
        <h3 className="text-text font-semibold mb-1">Mentoring Session</h3>
        <p className="text-sm text-muted mb-3">
          {fmtSlot(existingRequest.scheduledAt, existingRequest.startTime, existingRequest.endTime)}
        </p>
        <span className={`inline-block text-xs px-3 py-1 rounded-full font-medium ${
          isActive ? 'bg-success/15 text-success' : 'bg-warning/15 text-warning'
        }`}>
          {statusLabel[existingRequest.status] || existingRequest.status}
        </span>
      </div>
    );
  }

  if (booked) {
    return (
      <div className="border border-chart/30 rounded-xl p-6 bg-chart/5">
        <h3 className="text-text font-semibold mb-1">Session Request Sent</h3>
        <p className="text-muted text-sm">Your request has been sent to the mentor. You'll receive an email once they respond.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="border border-line rounded-xl p-6">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-chart border-t-transparent rounded-full animate-spin" />
          <span className="text-muted text-sm">Loading available slots…</span>
        </div>
      </div>
    );
  }

  const handleBook = async () => {
    if (!confirm) return;
    setBooking(true);
    setError('');
    try {
      await bookSlot(confirm.mentor._id, confirm.slot.scheduledAt, confirm.slot.startTime, confirm.slot.endTime);
      setBooked(true);
      setConfirm(null);
      onBooked?.();
    } catch (e: any) {
      setError(e?.message || 'Failed to book slot. Please try again.');
    } finally {
      setBooking(false);
    }
  };

  return (
    <>
      <div className="border border-line rounded-xl p-6">
        <h3 className="text-text font-semibold mb-1">Book a Mentoring Session</h3>
        <p className="text-muted text-sm mb-5">Select an available slot from a mentor below.</p>

        {error && <p className="text-danger text-sm mb-4">{error}</p>}

        {mentors.length === 0 ? (
          <p className="text-muted text-sm">No mentors have set availability yet. Check back soon.</p>
        ) : (
          <div className="space-y-5">
            {mentors.map(m => {
              const available = m.slots.filter(s => !s.booked);
              if (!available.length) return null;
              return (
                <div key={m.mentor._id} className="border border-line rounded-xl p-4">
                  <div className="flex items-start gap-3 mb-4">
                    {m.mentor.avatar ? (
                      <img src={m.mentor.avatar} alt={m.mentor.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-chart/20 flex items-center justify-center flex-shrink-0 text-chart font-semibold text-sm">
                        {m.mentor.name.charAt(0)}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-text font-semibold text-sm">{m.mentor.name}</p>
                      {(m.mentor.title || m.mentor.company) && (
                        <p className="text-muted text-xs">{[m.mentor.title, m.mentor.company].filter(Boolean).join(' · ')}</p>
                      )}
                      {m.mentor.expertise.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {m.mentor.expertise.slice(0, 3).map(tag => (
                            <span key={tag} className="text-xs px-2 py-0.5 bg-chart/10 text-chart rounded-full">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {m.slots.map((slot, idx) => (
                      <button
                        key={idx}
                        disabled={slot.booked}
                        onClick={() => !slot.booked && setConfirm({ mentor: m.mentor, slot })}
                        className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                          slot.booked
                            ? 'border-line text-muted cursor-not-allowed opacity-50'
                            : 'border-chart/40 text-chart hover:bg-chart/10 cursor-pointer'
                        }`}
                      >
                        {slot.booked ? 'Booked' : fmtSlot(slot.scheduledAt, slot.startTime, slot.endTime)}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-shell border border-line rounded-2xl p-8 w-full max-w-sm">
            <h3 className="text-text font-semibold text-lg mb-1">Confirm Booking</h3>
            <p className="text-muted text-sm mb-4">You're booking a session with:</p>
            <div className="bg-surface rounded-xl p-4 mb-6">
              <p className="text-text font-medium text-sm">{confirm.mentor.name}</p>
              <p className="text-muted text-xs mt-1">{fmtSlot(confirm.slot.scheduledAt, confirm.slot.startTime, confirm.slot.endTime)}</p>
            </div>
            {error && <p className="text-danger text-sm mb-3">{error}</p>}
            <div className="flex gap-3">
              <Button variant="ghost" size="sm" onClick={() => setConfirm(null)} disabled={booking}>Cancel</Button>
              <Button variant="primary" size="sm" onClick={handleBook} disabled={booking}>
                {booking ? 'Booking…' : 'Confirm'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MentorSlotBooker;
